import {Component, OnDestroy, OnInit} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {gql} from '@apollo/client';
import {Subscription, combineLatest} from 'rxjs';
import {
  ListComponent_getClients,
  ListComponent_getClients_clients_edges_node,
  ListComponent_getClientsVariables,
} from './__generated__/ListComponent_getClients';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {catchError, debounceTime, map, startWith, switchMap} from 'rxjs/operators';
import {ClientService} from '../shared/services/client.service';
import {ClientListComponent_getUserRole} from './__generated__/ClientListComponent_getUserRole';
import {ArchiveStateEnum, UserRole} from '../shared/graphql/globalTypes';
import {ErrorService} from '../shared/services/error.service';

const GET_USER_ROLE = gql`
    query ClientListComponent_getUserRole {
      me {
        id
        role
      }
    }
`;

const GET_CLIENTS = gql`
  query ListComponent_getClients($search: String, $state: ArchiveStateEnum) {
    clients(search: $search, orderBy: ["-created"], state: $state) {
      edges {
        node {
          id # Required
          fullname
          username # Phone number in case of clients
          created
          firstName
          lastName
          courseStart
          paused
          archived
          day
          coach {
            id
            firstName
            lastName
          }
          clientFanships {
            id
          }
        }
      }
      totalCount
    }
  }
`;

@Component({
  selector: 'app-client-list',
  templateUrl: './client-list.component.html',
  styleUrls: ['./client-list.component.scss'],
})
export class ClientListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  users: ListComponent_getClients_clients_edges_node[] | null = null;

  showCoach = false;
  searchControl = new FormControl('');
  showArchivedControl = new FormControl(false);

  search$ = this.searchControl.valueChanges.pipe(
    debounceTime(300),
    startWith(this.searchControl.value),
    map(searchTerm => searchTerm as string),
  );

  showArchived$ = this.showArchivedControl.valueChanges.pipe(
    startWith(this.showArchivedControl.value),
    map(showArchived => showArchived as boolean),
  );

  constructor(
    private apollo: Apollo,
    private router: Router,
    private route: ActivatedRoute,
    private clientService: ClientService,
    private errorHandler: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.apollo
        .watchQuery<ClientListComponent_getUserRole>({
          query: GET_USER_ROLE,
        })
        .valueChanges.subscribe(result => {
          // Coaches can only view own clients so its not great to show this as well.
          if (result.data.me.role != UserRole.COACH) {
            this.showCoach = true;
          }
        }),
      combineLatest([this.search$, this.showArchived$])
        .pipe(
          switchMap(([search, showArchived]) => {
            return this.apollo.watchQuery<
              ListComponent_getClients,
              ListComponent_getClientsVariables
            >({
              query: GET_CLIENTS,
              variables: {
                search: search,
                state: showArchived ? ArchiveStateEnum.ALL : ArchiveStateEnum.ACTIVE,
              },
            }).valueChanges;
          }),
          catchError(error => this.errorHandler.handleApollo(error)),
        )
        .subscribe(result => {
          this.users = result.data.clients.edges.map(e => e.node);
        }),
    );
  }

  onNavigate(id: string): void {
    this.router.navigate(['../clients', id], {
      relativeTo: this.route,
    });
  }

  parseDate(dateString: string): string {
    const createdDate = new Date(dateString).toISOString();
    const year = createdDate.slice(0, 4);
    const month = createdDate.slice(5, 7);
    const day = createdDate.slice(8, 10);

    return `${year}-${month}-${day}`;
  }

  convertDayToDisplay(day: number) {
    return this.clientService.convertDayToDisplay(day);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
